import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying');
  const [message, setMessage] = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMessage('Missing verification token.');
      return;
    }
    (async () => {
      const r = await verifyEmail(token);
      if (r.success) {
        setState('success');
        setMessage(r.message || 'Email verified successfully.');
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setState('error');
        setMessage(r.error || 'Verification failed.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-8 max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">🍄</span>
          </div>
          <h1 className="text-2xl font-extrabold text-[#1A2620] font-['Manrope']">Mushroom Farm</h1>
        </div>

        {state === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-[#2C4A3B] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1A2620] mb-2">Verifying your email…</h2>
            <p className="text-sm text-[#5C6B63]">Just a moment.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#EDF5EE] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-[#3A7D44]" />
            </div>
            <h2 className="text-xl font-bold text-[#1A2620] mb-2" data-testid="verify-success-title">Email Verified!</h2>
            <p className="text-sm text-[#5C6B63] mb-6">{message} Redirecting to login…</p>
            <Link to="/login" className="inline-block bg-[#2C4A3B] text-white hover:bg-[#3E6652] rounded-md px-6 py-2.5 font-medium text-sm">Go to Login</Link>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="w-16 h-16 bg-[#FAEDED] rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-[#D05A4A]" />
            </div>
            <h2 className="text-xl font-bold text-[#1A2620] mb-2" data-testid="verify-error-title">Verification Failed</h2>
            <p className="text-sm text-[#5C6B63] mb-6">{message}</p>
            <Link to="/login" className="inline-block border border-[#E4DFD3] text-[#2C4A3B] hover:bg-[#F7F5F0] rounded-md px-6 py-2.5 font-medium text-sm">Back to Login</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
